/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    LogOut,
    Save,
    Trash2,
    Mail,
    Phone,
    Building2,
    Search,
    RefreshCw,
    Loader2,
    AlertCircle,
    BarChart3,
    Inbox,
    ArrowLeft,
    CheckCircle2,
    Undo2,
    History,
    Briefcase,
} from "lucide-react";
import { ASSETS } from "../lib/assets";
import Seo from "../components/dmb/Seo";

import { API_BASE } from "../lib/assets";
const API = API_BASE;
const TOKEN_KEY = "dmb_admin_token";

function authHeaders() {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
}

// ---------- Login ----------
function Login({ onSuccess }) {
    const [token, setToken] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await axios.post(`${API}/admin/login`, { token });
            localStorage.setItem(TOKEN_KEY, token);
            onSuccess();
        } catch {
            setErr("Token invalide. Veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] px-6">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
            >
                <div className="flex justify-center mb-6">
                    <img src={ASSETS.logoLight} alt="DMB Partners" className="h-12" />
                </div>
                <h1 className="text-2xl font-extrabold text-[#2D2D2D] text-center">
                    Console d'administration
                </h1>
                <p className="text-center text-sm text-[#666] mt-2 mb-7">
                    Saisissez votre token d'administration.
                </p>
                <input
                    data-testid="admin-token-input"
                    type="password"
                    autoFocus
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Token admin"
                    className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-[#3CB650] focus:ring-4 focus:ring-[#3CB650]/15 outline-none text-[15px] text-[#2D2D2D]"
                />
                {err && (
                    <div
                        data-testid="admin-login-error"
                        className="mt-3 flex items-center gap-2 text-sm text-red-600"
                    >
                        <AlertCircle className="w-4 h-4" />
                        {err}
                    </div>
                )}
                <button
                    type="submit"
                    data-testid="admin-login-submit"
                    disabled={loading || !token}
                    className="btn-primary w-full justify-center mt-6 !py-3.5 disabled:opacity-60"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
                </button>
                <div className="mt-6 text-center">
                    <Link
                        to="/"
                        className="text-xs text-[#888] hover:text-[#3CB650] inline-flex items-center gap-1"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Retour au site
                    </Link>
                </div>
            </form>
        </div>
    );
}

// ---------- Chiffres de l'accueil (maquette 06a) ----------
const OP_FIELDS = [
    { key: "type", label: "Type", max: 80, placeholder: "Refinancement · RCF" },
    { key: "montant", label: "Montant", max: 30, placeholder: "5 M€" },
    { key: "secteur_ca", label: "Secteur · CA", max: 120, placeholder: "Téléphonie · 30 M€ de CA" },
    { key: "banques", label: "Banques", max: 60, placeholder: "3 banques" },
    { key: "mention", label: "Mention", max: 60, placeholder: "Conseil de la société · 2026" },
];

const inputCls =
    "w-full h-11 px-3 rounded-lg border border-gray-200 focus:border-[#3CB650] focus:ring-4 focus:ring-[#3CB650]/15 outline-none text-[15px] text-[#2D2D2D]";

function HomeFiguresEditor() {
    const [figures, setFigures] = useState([]);
    const [operations, setOperations] = useState([]);
    const [saved, setSaved] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [err, setErr] = useState("");

    const loadHistory = async () => {
        const hist = await axios.get(`${API}/admin/settings/home-figures/history`, {
            headers: authHeaders(),
        });
        setHistory(hist.data.history || []);
    };

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const cur = await axios.get(`${API}/settings/home-figures`);
            const snapshot = { figures: cur.data.figures, operations: cur.data.operations };
            setFigures(snapshot.figures);
            setOperations(snapshot.operations);
            setSaved(snapshot);
            await loadHistory();
        } catch {
            setErr("Impossible de charger les chiffres de l'accueil.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const updateFig = (i, field, val) =>
        setFigures((prev) => prev.map((f, idx) => (idx === i ? { ...f, [field]: val } : f)));
    const updateOp = (i, field, val) =>
        setOperations((prev) => prev.map((o, idx) => (idx === i ? { ...o, [field]: val } : o)));

    const cancel = () => {
        if (saved) {
            setFigures(saved.figures);
            setOperations(saved.operations);
        }
        setErr("");
    };

    const validate = () => {
        for (const f of figures) {
            if (!String(f.value ?? "").trim() || !String(f.label ?? "").trim()) {
                return "Chaque chiffre doit avoir une valeur et un libellé.";
            }
        }
        for (const o of operations) {
            for (const fd of OP_FIELDS) {
                if (!String(o[fd.key] ?? "").trim()) {
                    return "Chaque opération doit renseigner ses 5 champs.";
                }
            }
        }
        return null;
    };

    const save = async () => {
        const v = validate();
        if (v) {
            setErr(v);
            return;
        }
        setSaving(true);
        setErr("");
        try {
            await axios.put(
                `${API}/admin/settings/home-figures`,
                { figures, operations },
                { headers: authHeaders() },
            );
            setSaved({ figures, operations });
            setSavedAt(new Date());
            await loadHistory();
        } catch {
            setErr("Échec de la sauvegarde. Vérifiez votre session.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[#666]">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
            </div>
        );
    }

    return (
        <div data-testid="admin-figures-editor" className="space-y-9">
            {/* Les 4 chiffres */}
            <section>
                <h2 className="text-[17px] font-bold text-[#2D2D2D] m-0">Les 4 chiffres de l'accueil</h2>
                <p className="text-[13px] text-[#888] mt-1 mb-4">
                    Valeur : 12 caractères maximum · Libellé : 80 caractères maximum. Tous les champs sont obligatoires.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {figures.map((f, i) => (
                        <div
                            key={i}
                            data-testid={`admin-figure-${i}`}
                            className="bg-white rounded-xl border border-gray-200 p-5"
                        >
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3CB650] mb-3">
                                Chiffre {i + 1}
                            </div>
                            <div className="mb-3">
                                <label className="block text-[12px] font-semibold text-[#555] mb-1.5">
                                    Valeur *
                                </label>
                                <input
                                    data-testid={`admin-figure-${i}-value`}
                                    type="text"
                                    required
                                    maxLength={12}
                                    value={f.value}
                                    onChange={(e) => updateFig(i, "value", e.target.value)}
                                    className={inputCls}
                                    placeholder="+65 M€"
                                />
                                <div className="text-right text-[11px] text-[#999] mt-1">
                                    {(f.value || "").length}/12
                                </div>
                            </div>
                            <div>
                                <label className="block text-[12px] font-semibold text-[#555] mb-1.5">
                                    Libellé *
                                </label>
                                <input
                                    data-testid={`admin-figure-${i}-label`}
                                    type="text"
                                    required
                                    maxLength={80}
                                    value={f.label}
                                    onChange={(e) => updateFig(i, "label", e.target.value)}
                                    className={inputCls}
                                    placeholder="de financements obtenus pour nos clients"
                                />
                                <div className="text-right text-[11px] text-[#999] mt-1">
                                    {(f.label || "").length}/80
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-dashed border-gray-200 flex items-baseline gap-1.5">
                                <span className="text-xs text-[#888]">Aperçu :</span>
                                <span className="font-extrabold text-[#2D2D2D] tabular-nums">{f.value}</span>
                                <span className="text-xs text-[#666]">{f.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Les 3 opérations */}
            <section>
                <h2 className="text-[17px] font-bold text-[#2D2D2D] m-0">Les 3 opérations</h2>
                <p className="text-[13px] text-[#888] mt-1 mb-4">
                    5 champs obligatoires par opération · Mention : 60 caractères maximum.
                </p>
                <div className="grid grid-cols-1 gap-5">
                    {operations.map((o, i) => (
                        <div
                            key={i}
                            data-testid={`admin-operation-${i}`}
                            className="bg-white rounded-xl border border-gray-200 p-5"
                        >
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3CB650] mb-4">
                                Opération {i + 1}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {OP_FIELDS.map((fd) => (
                                    <div key={fd.key} className={fd.key === "secteur_ca" ? "sm:col-span-2 lg:col-span-1" : ""}>
                                        <label className="block text-[12px] font-semibold text-[#555] mb-1.5">
                                            {fd.label} *
                                        </label>
                                        <input
                                            data-testid={`admin-op-${i}-${fd.key.replace(/_/g, "-")}`}
                                            type="text"
                                            required
                                            maxLength={fd.max}
                                            value={o[fd.key]}
                                            onChange={(e) => updateOp(i, fd.key, e.target.value)}
                                            className={inputCls}
                                            placeholder={fd.placeholder}
                                        />
                                        <div className="text-right text-[11px] text-[#999] mt-1">
                                            {(o[fd.key] || "").length}/{fd.max}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {err && (
                <div data-testid="admin-figures-error" className="flex items-center gap-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    {err}
                </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-4 pt-1">
                <button
                    data-testid="admin-figures-save"
                    onClick={save}
                    disabled={saving}
                    className="btn-primary !py-3 !px-6 disabled:opacity-60"
                >
                    {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    Enregistrer
                </button>
                <button
                    data-testid="admin-figures-cancel"
                    onClick={cancel}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-200 hover:border-[#2D2D2D] text-[14px] font-semibold text-[#2D2D2D] transition-colors"
                >
                    <Undo2 className="w-4 h-4" />
                    Annuler les modifications
                </button>
                {savedAt && (
                    <div
                        data-testid="admin-figures-saved"
                        className="inline-flex items-center gap-1.5 text-sm text-[#3CB650] font-medium"
                    >
                        <CheckCircle2 className="w-4 h-4" />
                        Sauvegardé à {savedAt.toLocaleTimeString("fr-FR")}
                    </div>
                )}
            </div>

            {/* Historique */}
            <section className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                    <History className="w-4 h-4 text-[#3CB650]" />
                    <h2 className="text-[15px] font-bold text-[#2D2D2D] m-0">Historique des enregistrements</h2>
                </div>
                {history.length === 0 ? (
                    <p data-testid="admin-history-empty" className="text-[13px] text-[#888] m-0">
                        Aucun enregistrement pour l'instant. L'historique démarre à votre première sauvegarde.
                    </p>
                ) : (
                    <ul data-testid="admin-figures-history" className="divide-y divide-gray-100 m-0 p-0 list-none">
                        {history.map((h, i) => (
                            <li
                                key={h.saved_at}
                                data-testid={`admin-history-entry-${i}`}
                                className="py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"
                            >
                                <span className="text-[13px] font-semibold text-[#2D2D2D] whitespace-nowrap">
                                    {new Date(h.saved_at).toLocaleString("fr-FR")}
                                </span>
                                <span className="text-[13px] text-[#666] truncate">
                                    {(h.figures || []).map((f) => f.value).join(" · ")}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

// ---------- Contacts table ----------
function ContactsList() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await axios.get(`${API}/admin/contacts`, {
                headers: authHeaders(),
            });
            setContacts(res.data.contacts || []);
        } catch {
            setErr("Impossible de charger les demandes.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        if (!q) return contacts;
        const lo = q.toLowerCase();
        return contacts.filter(
            (c) =>
                (c.company_name || "").toLowerCase().includes(lo) ||
                (c.full_name || "").toLowerCase().includes(lo) ||
                (c.email || "").toLowerCase().includes(lo),
        );
    }, [contacts, q]);

    const removeOne = async (id) => {
        if (!window.confirm("Supprimer cette demande ?")) return;
        try {
            await axios.delete(`${API}/admin/contacts/${id}`, {
                headers: authHeaders(),
            });
            setContacts((prev) => prev.filter((c) => c.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch {
            alert("Échec de la suppression.");
        }
    };

    return (
        <div data-testid="admin-contacts">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-5">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
                    <input
                        data-testid="admin-contacts-search"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher entreprise, nom, email..."
                        className="w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 focus:border-[#3CB650] focus:ring-4 focus:ring-[#3CB650]/15 outline-none text-[14px]"
                    />
                </div>
                <button
                    data-testid="admin-contacts-refresh"
                    onClick={load}
                    className="inline-flex items-center gap-2 px-4 h-11 rounded-lg border border-gray-200 hover:border-[#3CB650] hover:text-[#3CB650] text-[14px] font-medium text-[#2D2D2D] transition-colors"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Actualiser
                </button>
            </div>

            {err && (
                <div className="flex items-center gap-2 text-sm text-red-600 mb-4">
                    <AlertCircle className="w-4 h-4" />
                    {err}
                </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-contacts-table">
                        <thead>
                            <tr className="bg-[#F5F5F5] text-[12px] uppercase text-[#666] tracking-wider">
                                <th className="text-left px-5 py-3.5 font-semibold">Date</th>
                                <th className="text-left px-5 py-3.5 font-semibold">Entreprise</th>
                                <th className="text-left px-5 py-3.5 font-semibold">Contact</th>
                                <th className="text-left px-5 py-3.5 font-semibold">Besoin</th>
                                <th className="text-left px-5 py-3.5 font-semibold">CA</th>
                                <th className="text-left px-5 py-3.5 font-semibold">Email envoyé ?</th>
                                <th className="px-5 py-3.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-5 py-12 text-center text-[#888]">
                                        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                                        Chargement...
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-5 py-12 text-center text-[#888]">
                                        Aucune demande pour l'instant.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((c) => {
                                    const date = new Date(c.created_at);
                                    const okClient = (c.client_email_status || "").startsWith("sent");
                                    const okInternal = (c.internal_email_status || "").startsWith("sent");
                                    return (
                                        <tr
                                            key={c.id}
                                            data-testid={`admin-contact-row-${c.id}`}
                                            className="border-t border-gray-100 hover:bg-[#F5F5F5]/60 cursor-pointer"
                                            onClick={() => setSelected(c)}
                                        >
                                            <td className="px-5 py-4 text-[#444] whitespace-nowrap">
                                                {date.toLocaleDateString("fr-FR")}
                                                <div className="text-[11px] text-[#999]">
                                                    {date.toLocaleTimeString("fr-FR")}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 font-semibold text-[#2D2D2D]">
                                                {c.company_name}
                                            </td>
                                            <td className="px-5 py-4 text-[#444]">
                                                <div>{c.full_name}</div>
                                                <div className="text-[12px] text-[#777]">
                                                    {c.email}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-[#444]">{c.need || "—"}</td>
                                            <td className="px-5 py-4 text-[#444]">{c.revenue}</td>
                                            <td className="px-5 py-4">
                                                <div className="flex flex-col gap-1">
                                                    <Badge ok={okClient} label="Client" />
                                                    <Badge ok={okInternal} label="Interne" />
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button
                                                    data-testid={`admin-contact-delete-${c.id}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeOne(c.id);
                                                    }}
                                                    className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail drawer */}
            {selected && (
                <div
                    data-testid="admin-contact-detail"
                    className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
                    onClick={() => setSelected(null)}
                >
                    <div
                        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6 border-b border-gray-100 flex justify-between items-start">
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3CB650]">
                                    Demande de contact
                                </div>
                                <h3 className="mt-1 text-xl font-bold text-[#2D2D2D]">
                                    {selected.company_name}
                                </h3>
                                <div className="text-xs text-[#888] mt-1">
                                    {new Date(selected.created_at).toLocaleString("fr-FR")}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-[#666] hover:text-[#2D2D2D] text-sm"
                            >
                                Fermer
                            </button>
                        </div>
                        <div className="p-6 space-y-4 text-[14px]">
                            <Field icon={Building2} label="Entreprise" value={selected.company_name} />
                            <Field label="Chiffre d'affaires" value={selected.revenue} />
                            <Field label="Besoin" value={selected.need || "—"} />
                            <Field label="Contact" value={selected.full_name} />
                            <Field icon={Briefcase} label="Fonction" value={selected.job_title || "—"} />
                            <Field
                                icon={Mail}
                                label="Email"
                                value={
                                    <a href={`mailto:${selected.email}`} className="text-[#3CB650] hover:underline">
                                        {selected.email}
                                    </a>
                                }
                            />
                            <Field
                                icon={Phone}
                                label="Téléphone"
                                value={
                                    <a href={`tel:${selected.phone}`} className="text-[#3CB650] hover:underline">
                                        {selected.phone}
                                    </a>
                                }
                            />
                            <div>
                                <div className="text-[12px] font-semibold text-[#888] uppercase tracking-wider mb-1.5">
                                    Message
                                </div>
                                <div className="bg-[#F5F5F5] border-l-4 border-[#3CB650] rounded-lg p-4 text-[#333] whitespace-pre-wrap leading-relaxed">
                                    {selected.message}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-100">
                                <Badge ok={(selected.client_email_status || "").startsWith("sent")} label={`Client : ${selected.client_email_status || "—"}`} long />
                                <Badge ok={(selected.internal_email_status || "").startsWith("sent")} label={`Interne : ${selected.internal_email_status || "—"}`} long />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ icon: Icon, label, value }) {
    return (
        <div className="flex gap-3">
            {Icon && (
                <div className="w-9 h-9 rounded-lg bg-[#3CB650]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#3CB650]" />
                </div>
            )}
            <div className={Icon ? "" : "ml-12"}>
                <div className="text-[12px] font-semibold text-[#888] uppercase tracking-wider">
                    {label}
                </div>
                <div className="text-[#2D2D2D] font-medium mt-0.5">{value}</div>
            </div>
        </div>
    );
}

function Badge({ ok, label, long }) {
    const color = ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${color} ${long ? "max-w-full break-all" : ""}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
            {label}
        </span>
    );
}

// ---------- Main shell ----------
function Shell({ onLogout }) {
    const [tab, setTab] = useState("contacts");

    return (
        <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-shell">
            <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ASSETS.logoLight} alt="DMB Partners" className="h-10" />
                        <span className="hidden sm:inline text-sm font-bold text-[#3CB650] uppercase tracking-[0.18em]">
                            Admin
                        </span>
                    </Link>
                    <button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-[#2D2D2D] hover:bg-[#F5F5F5] transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        Se déconnecter
                    </button>
                </div>
                <div className="max-w-6xl mx-auto px-6 flex gap-1">
                    <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="admin-tab-contacts">
                        <Inbox className="w-4 h-4" />
                        Demandes reçues
                    </TabBtn>
                    <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} testid="admin-tab-stats">
                        <BarChart3 className="w-4 h-4" />
                        Chiffres de l'accueil
                    </TabBtn>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10">
                {tab === "contacts" && <ContactsList />}
                {tab === "stats" && <HomeFiguresEditor />}
            </main>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-5 py-3.5 text-[14px] font-semibold border-b-2 transition-colors ${
                active
                    ? "border-[#3CB650] text-[#3CB650]"
                    : "border-transparent text-[#666] hover:text-[#2D2D2D]"
            }`}
        >
            {children}
        </button>
    );
}

// ---------- Page entry ----------
export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (!t) {
            setChecking(false);
            return;
        }
        axios
            .post(`${API}/admin/login`, { token: t })
            .then(() => setAuthed(true))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
            })
            .finally(() => setChecking(false));
    }, []);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
    };

    if (checking) {
        return (
            <>
                <Seo
                    title="Console d'administration | DMB Partners"
                    description="Console d'administration du site DMB Partners."
                    path="/admin"
                    robots="noindex, nofollow"
                />
                <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
                    <Loader2 className="w-6 h-6 animate-spin text-[#3CB650]" />
                </div>
            </>
        );
    }

    if (!authed)
        return (
            <>
                <Seo
                    title="Console d'administration | DMB Partners"
                    description="Console d'administration du site DMB Partners."
                    path="/admin"
                    robots="noindex, nofollow"
                />
                <Login onSuccess={() => setAuthed(true)} />
            </>
        );
    return (
        <>
            <Seo
                title="Console d'administration | DMB Partners"
                description="Console d'administration du site DMB Partners."
                path="/admin"
                robots="noindex, nofollow"
            />
            <Shell onLogout={logout} />
        </>
    );
}
